// Typography picker for a name slot in outgoing email. There are TWO
// independent slots (Lissette 2026-07-21: "sí, sepáralo"), each rendered next
// to the name it styles:
//
//   brandFont   → the agency name in the centered header at the TOP of the
//                 email. Edited in the "Nombre de la Agencia" panel.
//   companyFont → the company line at the BOTTOM of the signature.
//                 Edited in "Mi Perfil → Compañía".
//
// Writes uiPrefs.<slot>* — persisted by the debounced userPrefs sync, so there
// is no Save button here. Mirrors resolveBrandFont() in
// deskte-api/src/utils/emailSignature.ts, INCLUDING the per-field fallback of
// companyFont → brandFont; keep the two in sync.

// Email-safe font stacks only. An email cannot load a webfont, so any other
// family silently falls back to Times on the recipient's machine and the
// agent's choice looks like it was ignored.
export const EMAIL_FONT_OPTIONS = [
  // Empty stack = declare no font-family at all, so the message inherits the
  // client's default face. NOT the same as picking Arial explicitly.
  { key: "",          label: "Default",         stack: "" },
  { key: "arial",     label: "Arial",           stack: "Arial, Helvetica, sans-serif" },
  { key: "helvetica", label: "Helvetica",       stack: "Helvetica, Arial, sans-serif" },
  { key: "verdana",   label: "Verdana",         stack: "Verdana, Geneva, sans-serif" },
  { key: "tahoma",    label: "Tahoma",          stack: "Tahoma, Verdana, sans-serif" },
  { key: "trebuchet", label: "Trebuchet MS",    stack: "'Trebuchet MS', Tahoma, sans-serif" },
  { key: "georgia",   label: "Georgia",         stack: "Georgia, 'Times New Roman', serif" },
  { key: "times",     label: "Times New Roman", stack: "'Times New Roman', Times, serif" },
  { key: "garamond",  label: "Garamond",        stack: "Garamond, Georgia, serif" },
  { key: "courier",   label: "Courier New",     stack: "'Courier New', Courier, monospace" },
];

// Mirrors resolveBrandFont() on the backend, which in turn reproduces the
// pre-picker rendering — an agent who never opens this sees no change.
export const BRAND_FONT_DEFAULTS = { family: "", size: 17, color: "#0a558c", bold: true };

// companyFont inherits brandFont field by field, so agents who styled the name
// while #1329 had a single setting keep their choice until they change it.
const SLOT_FALLBACKS = { brandFont: ["brandFont"], companyFont: ["companyFont", "brandFont"] };
// Keyed off `undefined`, not falsiness: picking "Predeterminada" for the
// company line stores "" and must NOT snap back to the header's family.
function pick(p, slot, field) {
  for (const prefix of (SLOT_FALLBACKS[slot] || SLOT_FALLBACKS.brandFont)) {
    if (p[prefix + field] !== undefined) return p[prefix + field];
  }
  return undefined;
}

export function brandFontOf(prefs, slot = "brandFont") {
  const p = prefs || {};
  const family = pick(p, slot, "Family");
  const size = Number(pick(p, slot, "Size"));
  const color = String(pick(p, slot, "Color") || "");
  const bold = pick(p, slot, "Bold");
  return {
    family: EMAIL_FONT_OPTIONS.some((o) => o.key === family) ? family : BRAND_FONT_DEFAULTS.family,
    size: Number.isFinite(size) && size >= 10 && size <= 32 ? Math.round(size) : BRAND_FONT_DEFAULTS.size,
    color: /^#[0-9a-f]{6}$/i.test(color) ? color : BRAND_FONT_DEFAULTS.color,
    bold: bold === undefined ? BRAND_FONT_DEFAULTS.bold : bold !== false,
  };
}

export function brandFontStyle(bf, size) {
  const stack = (EMAIL_FONT_OPTIONS.find((o) => o.key === bf.family) || {}).stack;
  return {
    fontFamily: stack || undefined,
    fontSize: (size == null ? bf.size : size) + "px",
    fontWeight: bf.bold ? 700 : 400,
    color: bf.color,
  };
}

const INP = {
  background: "var(--c-bg2,#0a1628)",
  border: "1px solid var(--c-border2,#1f3f60)",
  borderRadius: 7,
  color: "var(--c-white,#eef6ff)",
  padding: ".45rem .5rem",
  fontSize: ".78rem",
  fontFamily: "inherit",
};

/**
 * @param prefs        the userPrefs object (uiPrefs)
 * @param onSetPref    (key, value) => void — same setter the rest of prefs uses
 * @param previewName  the name to render in the preview
 * @param slot         "brandFont" (email header) | "companyFont" (signature line)
 * @param label        optional heading override
 */
export function BrandFontEditor({ prefs, onSetPref, previewName, lang, label, slot = "brandFont" }) {
  const es = lang === "es";
  const bf = brandFontOf(prefs, slot);
  const set = (k, v) => onSetPref && onSetPref(slot + k, v);
  const isHeader = slot === "brandFont";
  const effSize = isHeader ? bf.size : Math.min(bf.size, 18);

  return (
    <div>
      <label style={{ display: "block", fontSize: ".68rem", fontWeight: 700, color: "var(--c-text2,#7aa3c0)", marginBottom: ".3rem" }}>
        {label || (isHeader
          ? (es ? "Letra en el encabezado de los correos" : "Font in the email header")
          : (es ? "Letra en la firma de los correos" : "Font in the email signature"))}
      </label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", alignItems: "center" }}>
        <select value={bf.family} onChange={(e) => set("Family", e.target.value)} style={{ ...INP, minWidth: 150 }}>
          {EMAIL_FONT_OPTIONS.map((o) => (
            <option key={o.key} value={o.key}>{o.key === "" ? (es ? "Predeterminada" : "Default") : o.label}</option>
          ))}
        </select>
        {/* Clamped for the signature slot so a size inherited from the header
            (which allows up to 28px) still matches one of the options below —
            an unmatched value renders the select blank. */}
        <select value={effSize} onChange={(e) => set("Size", Number(e.target.value))} style={{ ...INP, minWidth: 90 }}>
          {/* The signature line sits next to a 14px agent name, so the backend
              caps it at 18px — offer only what it will honour instead of
              letting the agent pick 28 and wonder why nothing changed. */}
          {(isHeader ? [12, 13, 14, 15, 16, 17, 18, 20, 22, 24, 28] : [11, 12, 13, 14, 15, 16, 17, 18]).map((n) => <option key={n} value={n}>{n} px</option>)}
        </select>
        <input type="color" value={bf.color} onChange={(e) => set("Color", e.target.value)}
          title={es ? "Color" : "Color"}
          style={{ width: 40, height: 34, padding: 2, background: "transparent", border: "1px solid var(--c-border2,#1f3f60)", borderRadius: 7, cursor: "pointer" }} />
        <button type="button" onClick={() => set("Bold", !bf.bold)}
          title={es ? "Negrita" : "Bold"}
          style={{ width: 38, height: 34, borderRadius: 7, cursor: "pointer", fontWeight: 800, fontSize: ".9rem", fontFamily: "inherit",
            background: bf.bold ? "rgba(0,212,255,.14)" : "transparent",
            border: "1px solid " + (bf.bold ? "rgba(0,212,255,.45)" : "var(--c-border2,#1f3f60)"),
            color: bf.bold ? "var(--c-cyan,#00d4ff)" : "var(--c-text2,#7aa3c0)" }}>B</button>
      </div>
      {/* White backdrop: this is how it lands in the recipient's inbox, not how
          it looks against the dark app shell. */}
      <div style={{ marginTop: ".55rem", background: "#fff", borderRadius: 8, padding: ".7rem .9rem", textAlign: "center" }}>
        <div style={brandFontStyle(bf, effSize)}>{previewName || (es ? "Nombre de tu agencia" : "Your agency name")}</div>
      </div>
      <div style={{ fontSize: ".66rem", color: "var(--c-text3,#45688a)", marginTop: 4, lineHeight: 1.45 }}>
        {(isHeader
          ? (es
            ? "Así se ve este nombre arriba del todo, en el encabezado de los correos que envías. La firma del final se configura aparte, en Mi Perfil → Compañía."
            : "This is how this name looks at the very top, in the header of the emails you send. The signature at the bottom is set separately, under My Profile → Company.")
          : (es
            ? "Así se ve este nombre en la firma, al final de los correos que envías. El encabezado de arriba se configura aparte, donde escribes el nombre de la agencia."
            : "This is how this name looks in the signature, at the bottom of the emails you send. The header at the top is set separately, where you type the agency name."))
          + (es
            ? " Solo aparecen letras que Gmail y Outlook ya tienen instaladas — cualquier otra no se vería en la computadora de tu cliente."
            : " Only fonts Gmail and Outlook already have installed are offered — any other would not render on your client's machine.")}
      </div>
    </div>
  );
}
